import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Notification interface for UI notifications
 */
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: number;
  read: boolean;
}

/**
 * Theme mode type
 */
export type ThemeMode = 'light' | 'dark';

/**
 * UI state interface
 */
interface UIState {
  isOffline: boolean;
  notifications: Notification[];
  globalLoading: boolean;
  sidebarOpen: boolean;
  syncInProgress: boolean;
  syncProgress: number;
  lastSyncTimestamp: number | null;
  pendingActionsCount: number;
  theme: ThemeMode;
}

/**
 * Get initial theme from localStorage or system preference
 */
const getInitialTheme = (): ThemeMode => {
  // First, check localStorage
  const savedTheme = localStorage.getItem('theme-preference');
  if (savedTheme === 'dark' || savedTheme === 'light') {
    return savedTheme;
  }
  
  // Fall back to system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  
  // Default to light
  return 'light';
};

/**
 * Initial UI state
 */
const initialState: UIState = {
  isOffline: !navigator.onLine,
  notifications: [],
  globalLoading: false,
  sidebarOpen: false,
  syncInProgress: false,
  syncProgress: 0,
  lastSyncTimestamp: null,
  pendingActionsCount: 0,
  theme: getInitialTheme(),
};

/**
 * UI slice for managing application-wide UI state
 * 
 * Manages theme preferences, offline status, notifications, sync state,
 * and other global UI concerns with localStorage persistence for theme.
 */
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    /**
     * Set offline status
     */
    setOfflineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },

    /**
     * Add a notification
     */
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp' | 'read'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        read: false,
      };
      state.notifications.unshift(notification);
    },

    /**
     * Mark notification as read
     */
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },

    /**
     * Mark all notifications as read
     */
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
    },

    /**
     * Remove a notification
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },

    /**
     * Clear all notifications
     */
    clearNotifications: (state) => {
      state.notifications = [];
    },

    /**
     * Set global loading state
     */
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.globalLoading = action.payload;
    },

    /**
     * Toggle sidebar open/closed
     */
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    /**
     * Set sidebar open state
     */
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    /**
     * Set sync in progress state
     */
    setSyncInProgress: (state, action: PayloadAction<boolean>) => {
      state.syncInProgress = action.payload;
      if (!action.payload) {
        state.syncProgress = 0;
      }
    },

    /**
     * Update sync progress (0-100)
     */
    setSyncProgress: (state, action: PayloadAction<number>) => {
      state.syncProgress = Math.min(100, Math.max(0, action.payload));
    },

    /**
     * Set last sync timestamp
     */
    setLastSyncTimestamp: (state, action: PayloadAction<number>) => {
      state.lastSyncTimestamp = action.payload;
    },

    /**
     * Set pending actions count
     */
    setPendingActionsCount: (state, action: PayloadAction<number>) => {
      state.pendingActionsCount = Math.max(0, action.payload);
    },

    /**
     * Increment pending actions count
     */
    incrementPendingActionsCount: (state) => {
      state.pendingActionsCount += 1;
    },

    /**
     * Decrement pending actions count
     */
    decrementPendingActionsCount: (state) => {
      state.pendingActionsCount = Math.max(0, state.pendingActionsCount - 1);
    },

    /**
     * Set theme mode
     * Persists theme preference to localStorage
     */
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.theme = action.payload;
      localStorage.setItem('theme-preference', action.payload);
      console.log('Theme set to:', action.payload);
    },

    /**
     * Toggle theme between light and dark modes
     * Persists theme preference to localStorage
     */
    toggleTheme: (state) => {
      const newTheme: ThemeMode = state.theme === 'light' ? 'dark' : 'light';
      console.log('Toggling theme from', state.theme, 'to', newTheme);
      state.theme = newTheme;
      localStorage.setItem('theme-preference', newTheme);
    },

    /**
     * Reset UI state to initial values
     */
    resetUIState: () => initialState,
  },
});

/**
 * Export actions
 */
export const {
  setOfflineStatus,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearNotifications,
  setGlobalLoading,
  toggleSidebar,
  setSidebarOpen,
  setSyncInProgress,
  setSyncProgress,
  setLastSyncTimestamp,
  setPendingActionsCount,
  incrementPendingActionsCount,
  decrementPendingActionsCount,
  setTheme,
  toggleTheme,
  resetUIState,
} = uiSlice.actions;

/**
 * Selectors
 */
export const selectIsOffline = (state: RootState) => state.ui.isOffline;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectUnreadNotifications = (state: RootState) => 
  state.ui.notifications.filter(n => !n.read);
export const selectUnreadNotificationsCount = (state: RootState) => 
  state.ui.notifications.filter(n => !n.read).length;
export const selectGlobalLoading = (state: RootState) => state.ui.globalLoading;
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectSyncInProgress = (state: RootState) => state.ui.syncInProgress;
export const selectSyncProgress = (state: RootState) => state.ui.syncProgress;
export const selectLastSyncTimestamp = (state: RootState) => state.ui.lastSyncTimestamp;
export const selectPendingActionsCount = (state: RootState) => state.ui.pendingActionsCount;
export const selectHasPendingActions = (state: RootState) => state.ui.pendingActionsCount > 0;
export const selectTheme = (state: RootState) => state.ui.theme;

/**
 * Export reducer
 */
export default uiSlice.reducer;